/**
 * Discrete scroll "sections" layered on top of the continuous 0-1 scroll progress.
 * Frame numbers are proportions of the baked camera clip's REAL timeline, authored in
 * Blender at 24fps: the clip is 13.875s long, which is EXACTLY 333 frames at 24fps
 * (13.875 * 24 = 333, no rounding) — confirmed against the GLB's own animation
 * accessor bounds (2026-07-06). Each section's `startFrame / TOTAL_FRAMES` gives its
 * start point in scroll progress.
 *
 * Frame 252 (the user-specified loop point) is NOT the clip's true end — it's 75.7%
 * through the 333-frame clip. Frames 252-333 are a real baked return-flight tail
 * (~3.4s) back toward Scene 1's start, which plays out during the "Scene 1 (loop)"
 * section before scroll wraps to progress 0 (`ScrollManager`'s own loop-to-top). An
 * earlier version of this file assumed a 252-frame-total 60fps reference timeline —
 * wrong on both counts (fps and total), which silently cut the return-flight tail out
 * of the experience entirely.
 *
 * Each section switches which scene's meshes are visible (`World.setActiveScene`)
 * and the camera's focal length. Entering most sections plays the white-smoke
 * `SceneTransition` first (mesh-swap + focal-length snap happen at the transition's
 * white peak, hidden from view); the Scene 3 -> Scene 4 boundary is a plain
 * continuous cut with no transition, and the loop (252 -> 0, back to Scene 1) plays
 * the transition with an intermediate 20mm focal length before settling on Scene 1's
 * 35mm (2026-07-05, user-specified timeline).
 */
export interface SceneSection {
  /** Debug-readout label. */
  name: string;
  /** Which scene's mesh group (`World.sceneGroups`) becomes visible. */
  sceneIndex: number;
  /** Start point, inclusive, out of `TOTAL_FRAMES` — proportion only. */
  startFrame: number;
  /** Camera focal length (mm) snapped on entry. */
  focalLength: number;
  /** Does ENTERING this section play the white-smoke transition? */
  transition: boolean;
}

/** The baked clip's authoring frame rate (Blender project setting) and its real
 *  duration (from the GLB's animation accessor `max` bound) — 13.875 * 24 = 333
 *  exactly, confirming both numbers. */
export const CLIP_FPS = 24;
// Re-confirmed 2026-07-17 for the current model `public/models/scene.glb`: the camera path timing has
// been UNCHANGED across every re-export so far — 15.417s
// @ 24fps = 370 frames (from the GLB animation accessor max bound). The scrub itself uses the model's
// real `Math.max(clip.duration)`; TOTAL_FRAMES is only for the section-boundary math below.
export const CLIP_DURATION_SECONDS = 15.417;
export const TOTAL_FRAMES = Math.round(CLIP_DURATION_SECONDS * CLIP_FPS); // 370

// Cinematic layout (2026-07-18, user's detailed frame spec). The intro fly-in plays frames 0→40
// (`settings.intro.autoplay`), then scroll drives these boundaries. FIVE content sections over the
// four MODEL scenes: Section 5 stays in model scene 4 (a final viewpoint), so `sceneIndex` for the
// cumulative reveal is 1,2,3,4,4. [ASSUMPTION — tell me if S5 should reveal something else.]
//
// `transition` = does entering this section play the WHITE FLASH? Per the user: flash only into
// S2 and S3, and into the loop/restart; S4 and S5 come in on a "magnetic" camera move with NO
// flash. Focal lengths 35/20/16/16/20 mm ease between sections so the lens change is imperceptible.
export const SECTIONS: readonly SceneSection[] = [
  { name: 'Chapter 01', sceneIndex: 1, startFrame: 40, focalLength: 35, transition: false },
  { name: 'Chapter 02', sceneIndex: 2, startFrame: 64, focalLength: 20, transition: true },
  { name: 'Chapter 03', sceneIndex: 3, startFrame: 125, focalLength: 16, transition: true },
  { name: 'Chapter 04', sceneIndex: 4, startFrame: 188, focalLength: 16, transition: false },
  { name: 'Chapter 05', sceneIndex: 4, startFrame: 260, focalLength: 20, transition: false },
  { name: 'Restart', sceneIndex: 1, startFrame: 300, focalLength: 35, transition: true },
];

/**
 * MAGNETIC transitions (2026-07-18, user spec). Once the user scrolls the camera to a
 * `triggerFrame`, scroll LOCKS and the camera auto-glides (eased) to `destFrame`, then hands
 * back. `flash` = play the white screen mid-glide (C3 and the restart); the others glide with
 * no flash. `restart` loops back to the intro. The C1→C2 boundary (frame 64) is NOT magnetic —
 * it's a plain white-flash boundary handled by `Experience.updateSection`.
 */
export interface MagneticPoint {
  name: string;
  /** Clip frame where the magnet grabs (scroll locks here). */
  triggerFrame: number;
  /** Clip frame the camera glides to, where the next chapter's content appears. */
  destFrame: number;
  /** Play the white flash mid-glide? */
  flash: boolean;
  /** Loop back to the intro (the final one). */
  restart?: boolean;
  /** Per-point glide duration override (seconds). Omitted = `settings.scenes.magnetDurationSec`.
   *  C1→C2 carries a LONGER one (2026-07-22, user: "40 to 64 must go smooth even if the user
   *  scrolls fast — the pull slows") so that opening move always reads calm. */
  durationSec?: number;
  /** Clip frame during the glide at which the destination chapter's FOCAL LENGTH starts
   *  easing in. Omitted = from the grab. (2026-07-21, user: C1→C2's focal change should run
   *  across frames 60→64 — the tail of the pull — not the whole glide.) */
  focalFrame?: number;
  /** Index into `SECTIONS` this magnet lands on (its chapter). */
  sectionIndex: number;
}

// FULL TEXT/PULL CHOREOGRAPHY (2026-07-21 v2, user spec). One uniform rule: at each trigger
// the CURRENT chapter's text fades out and the camera is pulled; the destination chapter's
// text animates in ONLY once the camera has fully STOPPED at the destination ("when camera
// reaches that point, then you animate — not before"):
//   50 → 64   C1 text out at 50; focal 35→20 eases across frames 60→64; C2 text in at stop
//  108 → 130  C2 text out at 108; C3 text in when the camera stops at 130
//  170 → 188  C3 text out at 170; C4 text in at 188 stop
//  240 → 260  C4 text out at 240; C5 text in at 260 stop
//  300 → 370  C5 text out at 300; camera glides to the clip's END, the white screen rises
//             smoothly over the stop, and under it everything resets to frame 0 → intro replays
// Scrolling BACKWARD mirrors this: boundary crossings fade the leaving text out and the
// earlier chapter's text back in (see Experience.updateSection), and every magnet re-arms once
// the user is back before its trigger — so the forward journey replays identically.
export const MAGNETIC_POINTS: readonly MagneticPoint[] = [
  // 4.2 → 7.0 (2026-09-01, user: "the door animations open so quickly, feels not natural"),
  // then 7.0 → 5.5 the same day ("still the pulling is slow — I want normal easing"): frames
  // 50→64 carry the baked door opening, so this duration IS the door speed once the magnet
  // owns the camera — long enough to feel drawn in, short enough to keep moving.
  { name: 'Chapter 02', triggerFrame: 50, destFrame: 64, flash: false, sectionIndex: 1, focalFrame: 60, durationSec: 5.5 },
  { name: 'Chapter 03', triggerFrame: 108, destFrame: 130, flash: true, sectionIndex: 2 },
  { name: 'Chapter 04', triggerFrame: 170, destFrame: 188, flash: false, sectionIndex: 3 },
  { name: 'Chapter 05', triggerFrame: 240, destFrame: 260, flash: false, sectionIndex: 4 },
  // Restart (2026-07-23 v2, user: "trigger at 320, the transition happens and the camera
  // resets to 0 in the background — quick and natural, not a long wait"): the grab is at 320,
  // the smoke closes in almost immediately (cover 322→336), and the reset fires the moment
  // the screen is fully matted — the glide toward 370 is just the motion UNDER the smoke and
  // never needs to finish.
  { name: 'Restart', triggerFrame: 320, destFrame: 370, flash: false, restart: true, sectionIndex: 5, durationSec: 2.8 },
];

/**
 * CONTINUOUS FOCAL LENGTH BY CLIP FRAME (2026-09-01, the no-magnets mode). Each section's lens
 * is reached by a smoothstep ramp across the `rampFrames` BEFORE its boundary and is exactly
 * the section's focal AT the boundary — so a checkpoint teleport, a magnet landing at
 * `startFrame + 0.5`, and plain scrolling all agree to the millimetre and nothing ever snaps.
 * Interpolated in LOG-FOCAL space (constant zoom ratio — how a physical lens pull feels; see
 * the magnet zoom in `Experience.updateMagnet` for the same rule). Pure function of frame, so
 * scrubbing backward plays every zoom in reverse identically.
 */
export function focalAtFrame(frame: number, rampFrames: number): number {
  const ramp = Math.max(rampFrames, 1e-3);
  let focal = SECTIONS[0]!.focalLength;
  for (let i = 1; i < SECTIONS.length; i += 1) {
    const s = SECTIONS[i]!;
    const t = (frame - (s.startFrame - ramp)) / ramp;
    if (t <= 0) break; // boundaries are ascending — nothing further can contribute
    const k = t >= 1 ? 1 : t * t * (3 - 2 * t);
    focal *= Math.pow(s.focalLength / focal, k);
  }
  return focal;
}

/** Section boundaries as scroll-progress fractions (0-1), matching `SECTIONS` order. */
const BOUNDARIES = SECTIONS.map((s) => s.startFrame / TOTAL_FRAMES);

/** Resolve which section a raw scroll progress (0-1) falls into. */
export function resolveSection(progress: number): { section: SceneSection; index: number } {
  let index = 0;
  for (let i = 0; i < BOUNDARIES.length; i += 1) {
    if (progress >= BOUNDARIES[i]!) index = i;
  }
  return { section: SECTIONS[index]!, index };
}
